// transformApiDataToTasksPageProps.ts

// Tipos para las props de la página de tareas y datos transformados
export type userAssigned = {
  name: string;
  image: string;
};

export type TasksPageProps = {
  id: number | string; // ID único para la key de React
  name: string; // El contenido/descripción de la tarea
  user: string; // Nombre o identificador del Lead o User asociado
  userImage?: string | null; // AVATAR para el Lead o User asociado (NUEVO)
  typeUser: "lead" | "user"; // Tipo de entidad asociada
  date: string; // Fecha de la tarea
  assigned: userAssigned; // Usuario asignado a la tarea
  tags: string[]; // Etiquetas para la tarea
  status: string; // Estado de la tarea (ej. "Pendiente", "Completada")
};

// --- INTERFACES PARA LA ESTRUCTURA DE DATOS DE LA API ACTUALIZADA ---

interface ApiLeadInfo {
  id: number;
  name: string;
  // Añade más campos si los necesitas aquí, ej: email?: string;
}

interface ApiUserTaskInfo {
  id: number;
  name: string;
  avatar?: string | null;
}

interface ApiAssignedUserInfo {
  id: number;
  name: string;
  avatar?: string | null;
}

interface ApiLabelItem {
  id: number;
  label: string;
}

interface ApiLeadTaskTag {
  id: number;
  label_item: ApiLabelItem;
  // task_id: number;
  // label_id: number;
}

// Interfaz actualizada para cada ApiTaskItem
interface ApiTaskItem {
  id: number;
  task: string;
  date: string;
  lead_id: number | null;
  user_id: number;
  assigned_id: number;
  created_at: string;
  updated_at: string;
  state: 0 | 1;
  late: 0 | 1 | 2;
  execution_day: string | null;
  user_task_id: number | null;

  lead: ApiLeadInfo | null;
  user_task: ApiUserTaskInfo | null;
  assigned: ApiAssignedUserInfo | null;
  leadtask?: ApiLeadTaskTag[]; // Es un array, puede estar vacío o no existir
}

// Función para transformar la data de la API a la estructura TasksPageProps
export const transformApiDataToTasksPageProps = (apiData: ApiTaskItem[] | undefined): TasksPageProps[] => {
  // Guarda para el error: si apiData es undefined o no es un array, retorna un array vacío.
  if (!apiData || !Array.isArray(apiData)) {
    return [];
  }

  return apiData.map((item) => {
    let userDisplay = "N/A";
    let typeUser: "lead" | "user" = "user";
    let userImage: string | null = null;

    if (item.lead && item.lead.name) {
      userDisplay = item.lead.name;
      typeUser = "lead";
      // Los leads no parecen tener avatar en tu JSON, generamos uno por defecto
      userImage = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.lead.name.substring(0, 2).trim() || 'LD')}&background=fuchsia&color=fff&bold=true`;
    } else if (item.user_task && item.user_task.name) {
      userDisplay = item.user_task.name;
      typeUser = "user";
      userImage = item.user_task.avatar // Usar avatar de user_task si existe
        ? item.user_task.avatar // Asegúrate que la URL sea completa si es relativa
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(item.user_task.name.substring(0, 2).trim() || 'US')}&background=indigo&color=fff&bold=true`;
    }

    const assignedUserInfo: userAssigned = {
      name: item.assigned?.name ?? `Asignado ID: ${item.assigned_id}`,
      image: item.assigned?.avatar // Usar avatar de assigned si existe
        ? item.assigned.avatar // Asegúrate que la URL sea completa
        : `https://ui-avatars.com/api/?name=${encodeURIComponent(item.assigned?.name?.substring(0, 2).trim() || 'AS')}&background=teal&color=fff&bold=true`,
    };

    const tags: string[] = [];

    // Solo etiquetas desde leadtask
    if (item.leadtask && Array.isArray(item.leadtask)) {
      item.leadtask.forEach(lt => {
        if (lt.label_item?.label && !tags.includes(lt.label_item.label)) {
          tags.push(lt.label_item.label);
        }
      });
    }

    const status = item.state === 0 ? "Pendiente" : "Completada";

    return {
      id: item.id,
      name: item.task,
      user: userDisplay,
      userImage: userImage, // Pasar la imagen del Lead/User
      typeUser: typeUser,
      date: item.date,
      assigned: assignedUserInfo,
      tags: [...new Set(tags)], // Asegurar etiquetas únicas
      status: status,
    };
  });
};